import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { PropsWithChildren, useMemo } from "react";

import { getToken } from "src/core/authentication/bearerToken";
import { withErrorHandling } from "src/core/graphql/client/withErrorHandling";
import { withXCSRFToken } from "src/core/graphql/client/withXCSRFToken";
import { useTenantIdFromRoute } from "src/core/hooks/routing/useTenantIdFromRoute";

const GRAPHQL_URI = "/v1/graphql";

const withAuthMiddleware = (): ApolloLink =>
  setContext((_, { headers }) => {
    const token = getToken();
    return {
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : null),
      },
    };
  });

const withTenantMiddleware = (tenantId: string | null): ApolloLink =>
  setContext((_, { headers }) => {
    if (!tenantId) return { headers };
    return {
      headers: {
        ...headers,
        CurrentTenantId: tenantId,
      },
    };
  });

// Adds retry policy to the graphql requests
export const withApolloRetryLink = () => {
  return new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error) => !!error,
    },
  });
};

export const TenantApolloProvider = ({ children }: PropsWithChildren) => {
  const tenantId = useTenantIdFromRoute();

  const client = useMemo(() => {
    return new ApolloClient({
      link: ApolloLink.from([
        withErrorHandling(),
        withApolloRetryLink(),
        withAuthMiddleware(),
        withTenantMiddleware(tenantId),
        withXCSRFToken(),
        createHttpLink({ uri: GRAPHQL_URI }),
      ]),
      cache: new InMemoryCache(),
    });
  }, [tenantId]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
